import React, { useEffect, useState } from 'react';
import MainLayout from '../components/layout/MainLayout';
import MaterialCard from '../components/cards/MaterialCard';
import { FiSearch, FiVideo, FiBook, FiGrid, FiFileText } from 'react-icons/fi';
import { supabase, getUserMaterials } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { normalizeImageUrl } from '../utils/imageUtils';

interface Material {
  id: string;
  titulo: string;
  descricao: string;
  tipo: 'vídeo' | 'livro' | 'revista';
  thumb_url: string | null;
  iframe: string;
}

const Home: React.FC = () => {
  const { user } = useAuth();
  const [materials, setMaterials] = useState<Material[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState<string>('todos');

  useEffect(() => {
    const fetchMaterials = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        console.log('Buscando materiais para o usuário:', user.id);
        
        // Usar a nova função centralizada para buscar materiais
        const { data, error: materialsError } = await getUserMaterials(user.id);
        
        if (materialsError) {
          console.error('Erro ao buscar materiais:', materialsError);
          throw materialsError;
        }
        
        console.log('Materiais encontrados:', data?.length || 0);
        if (data && data.length > 0) {
          console.log('Exemplo do primeiro material:', data[0]);
        }
        
        setMaterials(data || []);
      } catch (err: any) {
        console.error('Erro ao buscar materiais:', err);
        setError(err.message || 'Erro ao buscar conteúdos');
      } finally {
        setLoading(false);
      }
    };
    
    fetchMaterials();
  }, [user]);

  // Filtra os materiais por termo de busca e tipo
  const filteredMaterials = materials.filter((material) => {
    const matchesSearch = material.titulo.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         material.descricao.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filter === 'todos' || material.tipo === filter;
    
    return matchesSearch && matchesFilter;
  });

  return (
    <MainLayout>
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-branco-acinzentado font-raleway mb-2">
          Portal do Cliente
        </h1>
        <p className="text-gray-400">
          Bem-vindo(a), {user?.nome?.split(' ')[0]}! Explore os conteúdos disponíveis para você.
        </p>
      </div>
      
      {/* Barra de pesquisa e filtros */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-10 gap-4 bg-gray-900 p-4 rounded-lg shadow-md">
        <div className="relative flex-1 max-w-md">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FiSearch className="text-gray-500" />
          </div>
          <input
            type="text"
            placeholder="Buscar conteúdo..."
            className="input pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="flex gap-2">
          <button
            className={`btn btn-small ${
              filter === 'todos' 
                ? 'bg-vermelho-escuro text-white' 
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
            onClick={() => setFilter('todos')}
          >
            <FiGrid className="mr-1" />
            Todos
          </button>
          <button
            className={`btn btn-small ${
              filter === 'vídeo' 
                ? 'bg-vermelho-escuro text-white' 
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
            onClick={() => setFilter('vídeo')}
          >
            <FiVideo className="mr-1" />
            Vídeos
          </button>
          <button
            className={`btn btn-small ${
              filter === 'livro' 
                ? 'bg-vermelho-escuro text-white' 
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
            onClick={() => setFilter('livro')}
          >
            <FiBook className="mr-1" />
            Livros
          </button>
          <button
            className={`btn btn-small ${
              filter === 'revista' 
                ? 'bg-vermelho-escuro text-white' 
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
            onClick={() => setFilter('revista')}
          >
            <FiFileText className="mr-1" />
            Revistas
          </button>
        </div>
      </div>
      
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-vermelho-claro"></div>
        </div>
      ) : error ? (
        <div className="bg-red-900/30 border border-red-800 rounded-md p-4 text-center">
          <p className="text-red-200">{error}</p>
        </div>
      ) : filteredMaterials.length === 0 ? (
        <div className="text-center py-16 bg-gray-900/50 rounded-lg">
          <svg className="mx-auto h-16 w-16 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
          <h3 className="mt-4 text-xl font-medium text-gray-400 mb-2">Nenhum conteúdo encontrado</h3>
          <p className="text-gray-500 max-w-md mx-auto">
            {materials.length === 0
              ? 'Você ainda não tem conteúdos disponíveis. Entre em contato com o administrador.'
              : 'Tente ajustar os filtros ou os termos de busca.'}
          </p>
        </div>
      ) : (
        <div className="animate-slide-up">
          {/* Lista de todos os materiais em uma única grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredMaterials.map((material) => (
              <div id={`material-card-${material.id}`} key={material.id}>
                <MaterialCard material={material} />
              </div>
            ))}
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default Home; 